// src/components/states/berlin.js
export const berlin = {
  slug: "berlin",
  name: "Berlin",
  hero: {
    eyebrow: "Bundesland-Übersicht",
    title: "Erdwärme in Berlin",
    subtitle:
      "Kurze, praxisnahe Übersicht zu Förderung, Genehmigungen, Datenlage und typischen Untergrundthemen – als Einstieg vor Planung und Angeboten.",
    infoBox:
      "Hinweis: Anforderungen hängen vom Standort (z. B. Wasserschutzgebiet), der Heizleistung und dem System ab. Diese Seite ist Orientierung und ersetzt keinen Genehmigungsbescheid.",
  },
  seo: {
    title: "Erdwärme in Berlin – Förderung, Genehmigung & Untergrund | Erdwärme-Experten",
    description:
      "Kurzübersicht für Erdwärme in Berlin: Förderbedingungen (KfW), Genehmigungen (wasserrechtliche Erlaubnis), Datenlage, Untergrund und Hinweise zu Wasserschutzgebieten.",
    canonical: "https://www.erdwaerme-experten.de/bundeslaender/berlin",
  },

  // “Quick Facts” Cards oben (4 Kacheln)
  facts: [
    {
      title: "Förderung (Bund)",
      icon: "BadgeCheck",
      color: "from-rose-600 to-rose-500",
      bullets: [
        "Heizungstausch-Zuschuss über KfW Programm 458 für Bestandsgebäude",
        "30 % Grundförderung; persönliche Boni können die Quote erhöhen",
        "Maximal 80 %; kein separater Effizienzbonus für Erdwärme",
      ],
      note:
        "Förderstand 21.07.2026. Die tatsächliche Förderfähigkeit hängt von Gebäude, Eigentum, Einkommen und weiteren Voraussetzungen ab.",
      link: {
        label: "KfW 458 (offiziell)",
        href: "https://www.kfw.de/inlandsfoerderung/Privatpersonen/Bestehende-Immobilie/F%C3%B6rderprodukte/Heizungsf%C3%B6rderung-f%C3%BCr-Privatpersonen-Wohngeb%C3%A4ude-%28458%29/",
      },
    },
    {
      title: "Genehmigung",
      icon: "ShieldCheck",
      color: "from-slate-700 to-slate-600",
      bullets: [
        "Für Erdwärmesonden/Kollektoren ist i. d. R. eine wasserbehördliche Erlaubnis erforderlich",
        "Bohrbeginn muss mind. 2 Wochen vorher angezeigt werden",
        "Besondere Regeln/Restriktionen in Wasserschutzgebieten",
      ],
      note: "Berlin bündelt das Verfahren über die zuständige Wasserbehörde.",
      link: { label: "Serviceportal Berlin (Erlaubnis)", href: "https://service.berlin.de/dienstleistung/351504/" },
    },
    {
      title: "Anlagen > 30 kW",
      icon: "Drill",
      color: "from-indigo-700 to-indigo-600",
      bullets: [
        "Ab > 30 kW Heizleistung: zuerst Geothermal Response Test (GRT) beantragen",
        "Danach wasserrechtlicher Antrag",
        "Im Verfahren ist grundsätzlich eine thermohydrodynamische Modellierung vorgesehen",
      ],
      note: "Typisch für größere MFH-/Gewerbe-Projekte.",
      link: { label: "GRT beantragen (Berlin)", href: "https://service.berlin.de/dienstleistung/351507/" },
    },
    {
      title: "Untergrund & Wasser",
      icon: "Droplets",
      color: "from-emerald-700 to-emerald-600",
      bullets: [
        "Großräumig Norddeutsche Tiefebene / Beckenstrukturen",
        "Oberflächennah häufig Lockergesteine (Sande/Kiese) und teils hoher Grundwasserbezug",
        "Wasserschutzgebiete sind ein zentrales Planungsthema",
      ],
      note:
        "Für die Auslegung zählen lokal Wärmeleitfähigkeit, Grundwasser, Geologie und Platzverhältnisse.",
      link: { label: "Berlin: Erdwärmenutzung", href: "https://www.berlin.de/sen/uvk/umwelt/wasser-und-geologie/grundwasserbenutzung/erdwaermenutzung/" },
    },
  ],

  // Mittlere 3-Karten Sektion
  dataCards: [
    {
      title: "Welche Daten sind in Berlin relevant?",
      icon: "Layers",
      desc:
        "Für eine Vorprüfung zählen: Wärmeleitfähigkeit (λ), Schutzgebietsstatus, verfügbare Bohrtiefe(n), Platz & Bebauung.",
    },
    {
      title: "Was ist häufig der Engpass?",
      icon: "ThermometerSun",
      desc:
        "Nicht die Wärmepumpe – sondern Genehmigung/Restriktionen, Bohrlogistik im Bestand, und standortspezifische Untergrundparameter (inkl. Grundwasser).",
    },
    {
      title: "Was macht euer GeoReport konkret?",
      icon: "FileText",
      desc:
        "Standortbezogene Auswertung der verfügbaren Untergrunddaten, erste Dimensionierung (Sondenlänge/Kollektorfläche), Kosten- und Förderlogik als Vorentscheidung.",
    },
  ],

  // FAQ
  faq: [
    {
      q: "Brauche ich in Berlin immer eine Genehmigung für Erdsonden?",
      a: "Für Erdwärmesonden wird in Berlin in der Regel eine wasserbehördliche Erlaubnis benötigt. Zusätzlich ist der Bohrbeginn mindestens zwei Wochen vorher anzuzeigen. In Wasserschutzgebieten gelten besondere Anforderungen bzw. Einschränkungen.",
      links: [
        { label: "Erlaubnis (Serviceportal)", href: "https://service.berlin.de/dienstleistung/351504/" },
        { label: "Berlin – Erdwärmenutzung", href: "https://www.berlin.de/sen/uvk/umwelt/wasser-und-geologie/grundwasserbenutzung/erdwaermenutzung/" },
      ],
    },
    {
      q: "Was ist in Berlin die wichtigste Stop/Go-Frage?",
      a: "Sehr oft: Liegt das Grundstück in einem Wasserschutzgebiet oder in einem Bereich mit wasserrechtlichen Restriktionen? Das beeinflusst Genehmigung, Ausführung und teils die zulässige Tiefe.",
      links: [{ label: "Berlin – Erdwärmenutzung", href: "https://www.berlin.de/sen/uvk/umwelt/wasser-und-geologie/grundwasserbenutzung/erdwaermenutzung/" }],
    },
    {
      q: "Was gilt bei größeren Anlagen (Mehrfamilienhaus/Gewerbe)?",
      a: "Bei > 30 kW Heizleistung ist in Berlin zunächst ein Geothermal Response Test (GRT) zu beantragen und durchzuführen. Im anschließenden wasserrechtlichen Antragsverfahren ist grundsätzlich eine thermohydrodynamische Modellierung vorgesehen.",
      links: [{ label: "GRT beantragen", href: "https://service.berlin.de/dienstleistung/351507/" }],
    },
  ],

  cta: {
    title: "Berlin-Standort prüfen (datenbasiert)",
    text:
      "Du bekommst eine standortbezogene Vorabschätzung: Untergrundparameter (soweit verfügbar), erste Dimensionierung, Kosten-/Förderlogik und typische Genehmigungshinweise – als Grundlage für die nächsten Schritte.",
    primary: { label: "Jetzt Standort-Report erstellen", href: "/report", title: "Erdwärme Standort-Report für Berlin erstellen" },
    secondary: { label: "Erst Grundlagen ansehen", href: "/flache-geothermie" },
    footnote: "Tipp: Wenn du die Heizleistung grob kennst (< / > 30 kW), können wir das Verfahren schneller einordnen.",
  },
};
